import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import api from '@/services/api';
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  ChartBarSquareIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import styles from './LoginPage.module.css';

// Vistas de la tarjeta de autenticacion.
const VISTA_LOGIN = 'login';
const VISTA_SOLICITAR = 'solicitar';   // recuperar: pedir correo -> enviar OTP
const VISTA_CONFIRMAR = 'confirmar';   // recuperar: ingresar OTP + nueva contrasena

const PUNTOS_MARCA = [
  { icon: UserGroupIcon, texto: 'Expediente único por emprendedor, desde el registro hasta el fortalecimiento' },
  { icon: ChartBarSquareIcon, texto: 'Reportes y estadísticas en tiempo real para la toma de decisiones' },
  { icon: ShieldCheckIcon, texto: 'Trazabilidad completa: quién hizo qué y cuándo, en cada expediente' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { addToast } = useToast();

  const [vista, setVista] = useState(VISTA_LOGIN);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Login
  const [form, setForm] = useState({ correo: '', password: '' });

  // Recuperacion
  const [correoRecuperar, setCorreoRecuperar] = useState('');
  const [codigo, setCodigo] = useState('');
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');

  const irA = (nuevaVista) => {
    setVista(nuevaVista);
    setShowPassword(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form);
      addToast('Sesión iniciada correctamente', 'success');
      navigate('/dashboard');
    } catch (error) {
      addToast(error.message || 'Credenciales incorrectas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSolicitar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/recuperar/solicitar', { correo: correoRecuperar });
      addToast(res.message || 'Si el correo existe, enviaremos un código.', 'success');
      setCodigo('');
      setNuevaPassword('');
      setConfirmarPassword('');
      irA(VISTA_CONFIRMAR);
    } catch (error) {
      addToast(error.message || 'No se pudo procesar la solicitud', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async (e) => {
    e.preventDefault();
    if (nuevaPassword.length < 8) {
      addToast('La nueva contraseña debe tener al menos 8 caracteres', 'warning');
      return;
    }
    if (nuevaPassword !== confirmarPassword) {
      addToast('Las contraseñas no coinciden', 'warning');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/recuperar/confirmar', {
        correo: correoRecuperar,
        codigo: codigo.trim(),
        newPassword: nuevaPassword,
      });
      addToast(res.message || 'Contraseña restablecida correctamente', 'success');
      setForm({ correo: correoRecuperar, password: '' });
      setCodigo('');
      setNuevaPassword('');
      setConfirmarPassword('');
      irA(VISTA_LOGIN);
    } catch (error) {
      addToast(error.message || 'Código inválido o expirado', 'error');
    } finally {
      setLoading(false);
    }
  };

  const tituloVista = {
    [VISTA_LOGIN]: 'Bienvenido de nuevo',
    [VISTA_SOLICITAR]: 'Recupera tu acceso',
    [VISTA_CONFIRMAR]: 'Ingresa el código',
  }[vista];

  const subtituloVista = {
    [VISTA_LOGIN]: 'Ingresa tus credenciales para continuar',
    [VISTA_SOLICITAR]: 'Te enviaremos un código de verificación',
    [VISTA_CONFIRMAR]: 'Revisa tu bandeja de entrada',
  }[vista];

  return (
    <div className={styles.page}>
      {/* ============ PANEL DE MARCA (oculto en móvil) ============ */}
      <div className={styles.brandPanel}>
        <div className={styles.brandGlow} />
        <div className={styles.brandGrid} />
        <div className={styles.brandContent}>
          <div className={styles.brandLogo}>
            <img src="/logo_blanco.png" alt="SITRE" />
            <div className={styles.brandLogoText}>
              <strong>SITRE</strong>
              <span>Alcaldía de Soledad</span>
            </div>
          </div>

          <div className={styles.brandHeading}>
            <h2>Sistema de Trazabilidad de Emprendimiento</h2>
            <p>La plataforma de la Unidad de Competitividad para caracterizar, acompañar y fortalecer a los emprendedores del municipio.</p>
          </div>

          <ul className={styles.brandList}>
            {PUNTOS_MARCA.map(({ icon: Icon, texto }) => (
              <li key={texto}>
                <span className={styles.brandListIcon}><Icon width={18} /></span>
                <span>{texto}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className={styles.brandFooter}>© {new Date().getFullYear()} Alcaldía de Soledad · Unidad de Competitividad</p>
      </div>

      {/* ============ PANEL DEL FORMULARIO ============ */}
      <div className={styles.formPanel}>
        <div className={styles.formCard}>
          <div className={styles.mobileLogo}>
            <img src="/logo.png" alt="SITRE" />
          </div>

          <div className={styles.formHeading}>
            <h1>{tituloVista}</h1>
            <p>{subtituloVista}</p>
          </div>

          {/* ================= LOGIN ================= */}
          {vista === VISTA_LOGIN && (
            <form onSubmit={handleLogin} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Correo electrónico</label>
                <input
                  type="email"
                  value={form.correo}
                  onChange={(e) => setForm((p) => ({ ...p, correo: e.target.value }))}
                  placeholder="funcionario@soledad.gov.co"
                  required
                  autoFocus
                />
              </div>
              <div className={styles.formGroup}>
                <label>Contraseña</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    placeholder="••••••••"
                    required
                  />
                  <button type="button" className={styles.eyeButton} onClick={() => setShowPassword(!showPassword)} tabIndex="-1">
                    {showPassword ? <EyeSlashIcon className={styles.eyeIcon} /> : <EyeIcon className={styles.eyeIcon} />}
                  </button>
                </div>
              </div>
              <button type="submit" className={styles.btn} disabled={loading}>
                {loading ? 'Ingresando...' : 'Iniciar sesión'}
              </button>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => { setCorreoRecuperar(form.correo); irA(VISTA_SOLICITAR); }}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </form>
          )}

          {/* ============ RECUPERAR: PEDIR CORREO ============ */}
          {vista === VISTA_SOLICITAR && (
            <form onSubmit={handleSolicitar} className={styles.form}>
              <p className={styles.stepInfo}>
                Ingresa el correo de tu cuenta y te enviaremos un código de 6 dígitos para restablecer tu contraseña.
              </p>
              <div className={styles.formGroup}>
                <label>Correo electrónico</label>
                <input
                  type="email"
                  value={correoRecuperar}
                  onChange={(e) => setCorreoRecuperar(e.target.value)}
                  placeholder="funcionario@soledad.gov.co"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className={styles.btn} disabled={loading}>
                {loading ? 'Enviando...' : 'Enviar código'}
              </button>
              <button type="button" className={styles.backLink} onClick={() => irA(VISTA_LOGIN)}>
                <ArrowLeftIcon width={14} /> Volver a iniciar sesión
              </button>
            </form>
          )}

          {/* ======= RECUPERAR: CÓDIGO + NUEVA CONTRASEÑA ======= */}
          {vista === VISTA_CONFIRMAR && (
            <form onSubmit={handleConfirmar} className={styles.form}>
              <p className={styles.stepInfo}>
                Revisa tu correo <strong>{correoRecuperar}</strong> e ingresa el código que recibiste junto a tu nueva contraseña.
              </p>
              <div className={styles.formGroup}>
                <label>Código de 6 dígitos</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className={styles.codeInput}
                  required
                  autoFocus
                />
              </div>
              <div className={styles.formGroup}>
                <label>Nueva contraseña</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={nuevaPassword}
                    onChange={(e) => setNuevaPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    minLength={8}
                    required
                  />
                  <button type="button" className={styles.eyeButton} onClick={() => setShowPassword(!showPassword)} tabIndex="-1">
                    {showPassword ? <EyeSlashIcon className={styles.eyeIcon} /> : <EyeIcon className={styles.eyeIcon} />}
                  </button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Confirmar contraseña</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmarPassword}
                  onChange={(e) => setConfirmarPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  minLength={8}
                  required
                />
              </div>
              <button type="submit" className={styles.btn} disabled={loading}>
                {loading ? 'Guardando...' : 'Restablecer contraseña'}
              </button>
              <button type="button" className={styles.linkBtn} onClick={() => irA(VISTA_SOLICITAR)}>
                ¿No recibiste el código? Solicítalo de nuevo
              </button>
              <button type="button" className={styles.backLink} onClick={() => irA(VISTA_LOGIN)}>
                <ArrowLeftIcon width={14} /> Volver a iniciar sesión
              </button>
            </form>
          )}

          <p className={styles.hint}>Alcaldía de Soledad · Unidad de Competitividad</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
